/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth }   from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { useToast }  from '../context/ToastContext';
import { communitiesAPI, messagesAPI } from '../utils/api';
import { getAvatarUrl, formatTime } from '../utils/helpers';
import {
  Users, Hash, Plus, Send,
  Crown, Shield, X, Smile, Paperclip,
  LogOut
} from 'lucide-react';
import './CommunitiesPage.css';

export default function CommunitiesPage() {
  const { communityId, channel:channelParam } = useParams();
  const { user }   = useAuth();
  const { socket } = useSocket();
  const toast      = useToast();
  const navigate   = useNavigate();

  const [myCommunities, setMyCommunities] = useState([]);
  const [active,        setActive]        = useState(null);
  const [channel,       setChannel]       = useState(channelParam || 'general');
  const [messages,      setMessages]      = useState([]);
  const [input,         setInput]         = useState('');
  const [loading,       setLoading]       = useState(true);
  const [showCreate,    setShowCreate]    = useState(false);
  const [showMembers,   setShowMembers]   = useState(false);
  const [newName,       setNewName]       = useState('');
  const [newDesc,       setNewDesc]       = useState('');

  const messagesEnd = useRef(null);
  const myId = user?._id || user?.id;

  // Load my communities
  useEffect(() => {
    communitiesAPI.getMy().catch(()=>({data:[]})).then(r => {
      const list = r.data || [];
      setMyCommunities(list);
      if (communityId) {
        const found = list.find(c => (c._id||c.id)===communityId);
        if (found) setActive(found);
      } else if (list.length > 0) {
        setActive(list[0]);
      }
      setLoading(false);
    });
  }, [communityId]);

  // Load messages when active community/channel changes
  useEffect(() => {
    if (!active) return;
    const cId = active._id||active.id;
    messagesAPI.getCommunityMessages(cId, channel).catch(()=>({data:[]})).then(r => setMessages(r.data||[]));

    if (socket) socket.emit('community:join', { communityId:cId, channel });
  }, [active, channel, socket]);

  // Socket listener
  useEffect(() => {
    if (!socket) return;
    const handler = (msg) => {
      if (active && msg.community===(active._id||active.id) && msg.channel===channel) {
        setMessages(prev => [...prev, msg]);
      }
    };
    socket.on('community:message', handler);
    return () => socket.off('community:message', handler);
  }, [socket, active, channel]);

  useEffect(() => { messagesEnd.current?.scrollIntoView({behavior:'smooth'}); }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || !active) return;
    const content = input.trim();
    setInput('');
    const cId = active._id||active.id;

    if (socket) {
      socket.emit('community:message', { communityId:cId, channel, content, type:'text' });
    } else {
      try {
        const { data } = await messagesAPI.sendCommunityMessage(cId, channel, { content, type:'text' });
        setMessages(prev => [...prev, data]);
      } catch { toast.error('Failed to send'); }
    }
  };

  const createCommunity = async () => {
    if (!newName.trim()) { toast.error('Community name required'); return; }
    try {
      const { data } = await communitiesAPI.create({ name:newName.trim(), description:newDesc.trim() });
      setMyCommunities(prev => [data, ...prev]);
      setActive(data);
      setShowCreate(false);
      setNewName(''); setNewDesc('');
      toast.success('Community created! 🎉');
    } catch { toast.error('Failed to create community'); }
  };

  const leaveCommunity = async (comm) => {
    if (!window.confirm(`Leave ${comm.name}?`)) return;
    try {
      await communitiesAPI.leave(comm._id||comm.id);
      setMyCommunities(prev => prev.filter(c => (c._id||c.id)!==(comm._id||comm.id)));
      if (active && (active._id||active.id)===(comm._id||comm.id)) setActive(myCommunities[0]||null);
      toast.info('Left community');
    } catch { toast.error('Failed to leave'); }
  };

  const CHANNELS = active?.channels || [{name:'general',type:'text'},{name:'announcements',type:'text'}];

  if (loading) {
    return <div className="flex-center" style={{flex:1}}><div className="loader" style={{width:32,height:32,borderWidth:3}}/></div>;
  }

  return (
    <div className="communities-page">

      {/* ── Communities list (leftmost) ── */}
      <div className="comm-list-col">
        <div className="comm-list-header">
          <h2>Communities</h2>
          <button className="btn-icon" onClick={()=>setShowCreate(true)} title="Create community"><Plus size={18}/></button>
        </div>
        <div className="comm-list-body">
          {myCommunities.length === 0 ? (
            <div style={{padding:'24px 14px',textAlign:'center',color:'var(--text-500)'}}>
              <Users size={32} style={{opacity:.3,margin:'0 auto 10px'}}/>
              <p style={{fontSize:'.82rem',fontWeight:500}}>No communities yet</p>
              <button className="btn btn-primary btn-sm" style={{marginTop:12}} onClick={()=>navigate('/explore')}>
                Explore communities
              </button>
            </div>
          ) : myCommunities.map(c => (
            <div key={c._id||c.id} className={`comm-list-item ${active&&(active._id||active.id)===(c._id||c.id)?'active':''}`}
              onClick={()=>{setActive(c);setChannel('general');navigate(`/communities/${c._id||c.id}`);}}>
              <div className="comm-avatar" style={{background:c.color||'linear-gradient(135deg,#6C63FF,#FF4D8D)'}}>
                {c.avatar ? <img src={c.avatar} alt="" style={{width:'100%',height:'100%',borderRadius:'inherit',objectFit:'cover'}}/> : c.name?.[0]?.toUpperCase()}
              </div>
              <div className="comm-list-info">
                <p className="comm-list-name">{c.name}</p>
                <p className="comm-list-members">{c.memberCount||0} members</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Channels sidebar ── */}
      {active && (
        <div className="comm-channels-col">
          <div className="comm-header-info">
            <h3>{active.name}</h3>
            <p>{active.memberCount||0} members</p>
          </div>
          <div className="comm-channels-list">
            <p className="comm-section-label">Channels</p>
            {CHANNELS.map(ch => (
              <button key={ch.name} className={`comm-channel-item ${channel===ch.name?'active':''}`} onClick={()=>setChannel(ch.name)}>
                <Hash size={15}/> {ch.name}
              </button>
            ))}
          </div>
          <div className="comm-channels-footer">
            <button className="settings-nav-item" onClick={()=>setShowMembers(true)}>
              <Users size={16}/> Members
            </button>
            <button className="settings-nav-item" style={{color:'var(--red)'}} onClick={()=>leaveCommunity(active)}>
              <LogOut size={16}/> Leave
            </button>
          </div>
        </div>
      )}

      {/* ── Chat area ── */}
      {active ? (
        <div className="comm-chat-col">
          <div className="comm-chat-header">
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <Hash size={18} style={{color:'var(--text-400)'}}/>
              <span style={{fontWeight:700,fontSize:'.95rem'}}>{channel}</span>
            </div>
            <button className="btn-icon" onClick={()=>setShowMembers(true)}><Users size={17}/></button>
          </div>

          <div className="comm-messages">
            {messages.length===0 ? (
              <div className="empty-state">
                <Hash size={40}/>
                <h3>Welcome to #{channel}</h3>
                <p>This is the start of the #{channel} channel in {active.name}</p>
              </div>
            ) : messages.map(msg => {
              const sender = msg.sender || msg.senderInfo || {};
              const isMine = (sender._id||sender.id||sender)===myId;
              return (
                <div key={msg._id||msg.id} className="comm-msg-row">
                  <img src={getAvatarUrl(sender)} alt="" className="avatar" style={{width:36,height:36,flexShrink:0}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:7}}>
                      <span style={{fontWeight:700,fontSize:'.85rem'}}>{sender.displayName || (isMine?user.displayName:'User')}</span>
                      <span style={{fontSize:'.68rem',color:'var(--text-500)'}}>{formatTime(msg.createdAt)}</span>
                    </div>
                    <p style={{fontSize:'.88rem',color:'var(--text-200)',marginTop:2,lineHeight:1.5,wordBreak:'break-word'}}>{msg.content}</p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEnd}/>
          </div>

          <div className="comm-input-bar">
            <button className="btn-icon input-btn"><Paperclip size={19}/></button>
            <div className="input-wrap">
              <input className="chat-input" placeholder={`Message #${channel}`} value={input}
                onChange={e=>setInput(e.target.value)}
                onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();sendMessage();}}}/>
            </div>
            <button className="btn-icon input-btn"><Smile size={19}/></button>
            <button className={`send-btn ${input.trim()?'ready':''}`} onClick={sendMessage} disabled={!input.trim()}>
              <Send size={17}/>
            </button>
          </div>
        </div>
      ) : (
        <div className="empty-state" style={{flex:1}}>
          <Users size={48}/>
          <h3>No community selected</h3>
          <p>Select a community or create a new one to get started</p>
          <button className="btn btn-primary btn-sm" onClick={()=>setShowCreate(true)}>
            <Plus size={14}/> Create Community
          </button>
        </div>
      )}

      {/* ── Create modal ── */}
      {showCreate && (
        <div className="modal-overlay" onClick={()=>setShowCreate(false)}>
          <div className="modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Community</h2>
              <button className="btn-icon" onClick={()=>setShowCreate(false)}><X size={18}/></button>
            </div>
            <div className="form-group">
              <label>Community Name</label>
              <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="e.g. Tech Enthusiasts" autoFocus/>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea rows={3} value={newDesc} onChange={e=>setNewDesc(e.target.value)} placeholder="What's this community about?"/>
            </div>
            <button className="btn btn-primary w-full" onClick={createCommunity}>
              <Plus size={15}/> Create Community
            </button>
          </div>
        </div>
      )}

      {/* ── Members modal ── */}
      {showMembers && active && (
        <div className="modal-overlay" onClick={()=>setShowMembers(false)}>
          <div className="modal-box" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>Members ({active.memberCount||0})</h2>
              <button className="btn-icon" onClick={()=>setShowMembers(false)}><X size={18}/></button>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:4,maxHeight:400,overflowY:'auto'}}>
              {(active.members||[]).length===0 ? (
                <p style={{textAlign:'center',color:'var(--text-500)',padding:20,fontSize:'.85rem'}}>No members data available</p>
              ) : (active.members||[]).map((m,i) => (
                <div key={i} className="contact-item">
                  <img src={getAvatarUrl(m)} alt="" className="avatar" style={{width:38,height:38}}/>
                  <div className="contact-info">
                    <p className="contact-name">{m.displayName||'Member'}</p>
                  </div>
                  {m.role==='owner' && <Crown size={14} style={{color:'var(--yellow)'}}/>}
                  {m.role==='admin' && <Shield size={14} style={{color:'var(--primary-light)'}}/>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}